﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { api, type AssignmentRow, type OwnerRow } from "../app/api";
import { DeleteCodeConfirmModal } from "../components/common/DeleteCodeConfirmModal";
import { ServerDataTable } from "../components/tables/ServerDataTable";
import { isActionMenuEventTarget, useActionMenuAutoClose, useFloatingActionMenu } from "../hooks/useActionMenuAutoClose";

type StatusOption = "" | "aktiv" | "inaktiv";
type RecipientTypeOption = "" | "owner" | "authorized";
type AssignmentSortField = "objektnummer" | "einheitsnummer" | "eigentuemer_id";

const formatDateDE = (value?: string) => {
  if (!value) return "-";
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : value;
};

const formatOwnerDisplayName = (owner?: Partial<OwnerRow> | null) => {
  if (!owner) return "-";
  const parts = [owner.anrede, owner.titel, owner.vorname, owner.name_1, owner.name_2, owner.name_3]
    .map((part) => (part ?? "").trim())
    .filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  return owner.vollname?.trim() || "-";
};

const buildAssignmentValidityTooltip = (assignment: AssignmentRow) =>
  `EGT ab: ${formatDateDE(assignment.gueltig_ab)} | EGT bis: ${formatDateDE(assignment.gueltig_bis)}`;

type AssignmentForm = {
  objectId: string;
  unitId: string;
  ownerId: string;
  status: StatusOption;
  gueltig_ab: string;
  gueltig_bis: string;
};

type DeleteConfirmState = {
  id: string;
  label: string;
  code: string;
  input: string[];
  error: string;
  warning?: string;
} | null;

type OwnershipChangeState = {
  existing: AssignmentRow;
  vorgaengerGueltigBis: string;
} | null;

type AssignmentsPageProps = {
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
};

export const AssignmentsPage = ({ canCreate = false, canUpdate = false, canDelete = false }: AssignmentsPageProps) => {
  const queryClient = useQueryClient();
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [objectFilter, setObjectFilter] = useState("");
  const [status, setStatus] = useState<StatusOption>("aktiv");
  const [recipientType, setRecipientType] = useState<RecipientTypeOption>("");
  const [sortBy, setSortBy] = useState<AssignmentSortField>("objektnummer");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [showCreate, setShowCreate] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [isActionMenuHovered, setIsActionMenuHovered] = useState(false);
  const [createError, setCreateError] = useState("");
  const [editError, setEditError] = useState("");
  const [actionError, setActionError] = useState("");
  const [ownerSearchCreate, setOwnerSearchCreate] = useState("");
  const [ownerSearchEdit, setOwnerSearchEdit] = useState("");
  const [createOnlyUnassigned, setCreateOnlyUnassigned] = useState(false);
  const [editOnlyUnassigned, setEditOnlyUnassigned] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>(null);
  const [ownershipChange, setOwnershipChange] = useState<OwnershipChangeState>(null);
  const [form, setForm] = useState<AssignmentForm>({ objectId: "", unitId: "", ownerId: "", status: "", gueltig_ab: "", gueltig_bis: "" });
  const [editForm, setEditForm] = useState<null | ({ id: string } & AssignmentForm)>(null);

  const { data, isError } = useQuery({
    queryKey: ["assignments", page, pageSize, search, objectFilter, status, recipientType, sortBy, sortDir],
    queryFn: () =>
      api.assignments({
        page,
        pageSize,
        search,
        objectId: objectFilter || undefined,
        status: status || undefined,
        recipientType: recipientType || undefined,
        sortBy,
        sortDir
      }),
    placeholderData: (previousData) => previousData,
    staleTime: 15_000
  });

  const { data: objectsData } = useQuery({
    queryKey: ["objects-select"],
    queryFn: () => api.objects({ page: 1, pageSize: 200, sortBy: "objektnummer", sortDir: "asc" })
  });

  const { data: activeResolutions } = useQuery({
    queryKey: ["active-resolutions-lock"],
    queryFn: () => api.resolutions({ page: 1, pageSize: 500, status: "aktiv" })
  });
  const { data: uiSettings } = useQuery({
    queryKey: ["ui-settings-assignments"],
    queryFn: () => api.uiSettings(),
    staleTime: 60_000
  });

  const lockedObjectIds = useMemo(() => new Set((activeResolutions?.data ?? []).map((r) => r.objectId)), [activeResolutions?.data]);

  const { data: unitsData } = useQuery({
    queryKey: ["units-select", form.objectId],
    queryFn: () => api.units({ page: 1, pageSize: 200, objectId: form.objectId, status: "aktiv" }),
    enabled: !!form.objectId
  });

  const { data: ownersData } = useQuery({
    queryKey: ["owners-select", form.objectId],
    queryFn: () => api.owners({ page: 1, pageSize: 2000, objectId: form.objectId, status: "aktiv" }),
    enabled: !!form.objectId
  });

  const { data: editUnitsData } = useQuery({
    queryKey: ["units-select-edit", editForm?.objectId],
    queryFn: () => api.units({ page: 1, pageSize: 200, objectId: editForm!.objectId, status: "aktiv" }),
    enabled: !!editForm?.objectId
  });

  const { data: editOwnersData } = useQuery({
    queryKey: ["owners-select-edit", editForm?.objectId],
    queryFn: () => api.owners({ page: 1, pageSize: 2000, objectId: editForm!.objectId, status: "aktiv" }),
    enabled: !!editForm?.objectId
  });
  const { data: ownerAssignmentsData } = useQuery({
    queryKey: ["owners-assignment-overview"],
    queryFn: () => api.assignments({ page: 1, pageSize: 5000, status: "aktiv" })
  });
  const { data: ownersLookupData } = useQuery({
    queryKey: ["owners-lookup-all"],
    queryFn: () => api.owners({ page: 1, pageSize: 5000 })
  });

  const assignmentRefsByOwner = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const assignment of ownerAssignmentsData?.data ?? []) {
      const list = map.get(assignment.ownerId) ?? [];
      list.push(`${assignment.objektnummer}/${assignment.einheitsnummer}`);
      map.set(assignment.ownerId, list);
    }
    return map;
  }, [ownerAssignmentsData?.data]);

  const filteredCreateOwners = useMemo(() => {
    const rows = ownersData?.data ?? [];
    const needle = ownerSearchCreate.trim().toLowerCase();
    const searched = !needle
      ? rows
      : rows.filter((owner) =>
      `${owner.eigentuemer_id} ${owner.vollname} ${owner.email ?? ""}`.toLowerCase().includes(needle)
    );
    if (!createOnlyUnassigned) return searched;
    return searched.filter((owner) => (assignmentRefsByOwner.get(owner.id) ?? []).length === 0);
  }, [ownersData?.data, ownerSearchCreate, createOnlyUnassigned, assignmentRefsByOwner]);

  const filteredEditOwners = useMemo(() => {
    const rows = editOwnersData?.data ?? [];
    const needle = ownerSearchEdit.trim().toLowerCase();
    const searched = !needle
      ? rows
      : rows.filter((owner) =>
      `${owner.eigentuemer_id} ${owner.vollname} ${owner.email ?? ""}`.toLowerCase().includes(needle)
    );
    if (!editOnlyUnassigned) return searched;
    return searched.filter((owner) => (assignmentRefsByOwner.get(owner.id) ?? []).length === 0);
  }, [editOwnersData?.data, ownerSearchEdit, editOnlyUnassigned, assignmentRefsByOwner]);
  const createMutation = useMutation({
    mutationFn: (payload: {
      objectId: string;
      unitId: string;
      ownerId: string;
      status: "aktiv" | "inaktiv";
      gueltig_ab?: string;
      gueltig_bis?: string;
      uebernahme_von_assignment_id?: string;
      vorgaenger_gueltig_bis?: string;
    }) => api.createAssignment(payload),
    onSuccess: async () => {
      setCreateError("");
      setShowCreate(false);
      setOwnershipChange(null);
      setOwnerSearchCreate("");
      setForm({ objectId: "", unitId: "", ownerId: "", status: "", gueltig_ab: "", gueltig_bis: "" });
      await queryClient.invalidateQueries({ queryKey: ["assignments"] });
      setPage(1);
    },
    onError: (error) => setCreateError(error instanceof Error ? error.message : "Anlage fehlgeschlagen")
  });

  const editMutation = useMutation({
    mutationFn: () => {
      if (!editForm) throw new Error("Kein Datensatz ausgewählt");
      if (lockedObjectIds.has(editForm.objectId)) throw new Error("Zuordnung ist wegen aktivem Umlaufbeschluss gesperrt");
      return api.updateAssignment(editForm.id, {
        objectId: editForm.objectId,
        unitId: editForm.unitId,
        ownerId: editForm.ownerId,
        status: editForm.status as "aktiv" | "inaktiv",
        gueltig_ab: editForm.gueltig_ab || undefined,
        gueltig_bis: editForm.gueltig_bis || undefined
      });
    },
    onSuccess: async () => {
      setEditError("");
      setEditForm(null);
      setOwnerSearchEdit("");
      await queryClient.invalidateQueries({ queryKey: ["assignments"] });
    },
    onError: (error) => setEditError(error instanceof Error ? error.message : "Bearbeitung fehlgeschlagen")
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAssignment(id),
    onSuccess: async () => {
      setDeleteConfirm(null);
      setActionError("");
      await queryClient.invalidateQueries({ queryKey: ["assignments"] });
    },
    onError: (error) => setActionError(error instanceof Error ? error.message : "Löschen fehlgeschlagen")
  });

  useEffect(() => {
    setPage(1);
  }, [search, objectFilter, status, recipientType, sortBy, sortDir, pageSize]);

  useEffect(() => {
    setOpenActionMenuId(null);
  }, [page, search, objectFilter, status, recipientType, sortBy, sortDir, showCreate, editForm]);

  useEffect(() => {
    if (!openActionMenuId) return;
    const closeMenu = (event?: MouseEvent) => {
      if (event && isActionMenuEventTarget(event.target)) return;
      setOpenActionMenuId(null);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };
    window.addEventListener("mousedown", closeMenu);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("mousedown", closeMenu);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [openActionMenuId]);

  useActionMenuAutoClose({
    isOpen: !!openActionMenuId,
    isHovered: isActionMenuHovered,
    closeMenu: () => setOpenActionMenuId(null),
    timeoutMs: 3000
  });

  useEffect(() => {
    if (!openActionMenuId) setIsActionMenuHovered(false);
  }, [openActionMenuId]);

  const { actionMenuPosition, openActionMenuAtTrigger } = useFloatingActionMenu({
    openMenuId: openActionMenuId,
    setOpenMenuId: setOpenActionMenuId
  });

  const objectIdByObjektnummer = useMemo(
    () => new Map((objectsData?.data ?? []).map((o) => [o.objektnummer, o.id])),
    [objectsData?.data]
  );

  const ownerDisplayById = useMemo(() => {
    const map = new Map<string, string>();
    for (const owner of ownersLookupData?.data ?? []) {
      map.set(owner.id, formatOwnerDisplayName(owner));
    }
    return map;
  }, [ownersLookupData?.data]);

  const formatEinheitsnummer = (value: string) => {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return "";
    return /^\d+$/.test(trimmed) ? trimmed.padStart(4, "0") : trimmed;
  };

  const formatAssignmentNumber = (value: number | undefined) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? new Intl.NumberFormat("de-DE").format(numeric) : "-";
  };

  const formatAssignmentMea = (value: number | undefined) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0
      ? new Intl.NumberFormat("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(numeric)
      : "-";
  };

  const toggleSort = (field: AssignmentSortField) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(field);
    setSortDir("asc");
  };

  const sortLabel = (field: AssignmentSortField, label: string) => (
    <button
      type="button"
      onClick={() => toggleSort(field)}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        gap: 6
      }}
      title={sortBy === field ? `Sortierung: ${sortDir === "asc" ? "aufsteigend" : "absteigend"}` : "Sortierung aktivieren"}
    >
      <span>{label}</span>
      <span aria-hidden="true">{sortBy === field ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</span>
    </button>
  );

  const columns = useMemo(
    () => [
      {
        key: "objektnummer",
        label: sortLabel("objektnummer", "Objekt‑Nr"),
        width: 92,
        align: "center" as const,
        render: (r: AssignmentRow) => {
          const objectId = objectIdByObjektnummer.get(r.objektnummer);
          return objectId ? (
            <Link to={`/objects/${objectId}`} className="badge object-ref" style={{ textDecoration: "none" }}>
              {r.objektnummer}
            </Link>
          ) : (
            r.objektnummer
          );
        }
      },
      {
        key: "einheitsnummer",
        label: sortLabel("einheitsnummer", "Einheit‑Nr."),
        width: 96,
        align: "center" as const,
        render: (r: AssignmentRow) => formatEinheitsnummer(r.einheitsnummer)
      },
      { key: "eigentuemer_id", label: sortLabel("eigentuemer_id", "Eigentümer ID"), width: 184, align: "center" as const },
      {
        key: "eigentuemername",
        label: "Eigentümername",
        render: (r: AssignmentRow) => (
          <>
            {ownerDisplayById.get(r.ownerId) ?? r.eigentuemername ?? "-"}
            {r.isAuthorizedOwnerRecipient ? <span className="badge" style={{ marginLeft: 8 }}>Zustellungsbevollmächtigt</span> : null}
          </>
        )
      },
      {
        key: "eigentuemer_email",
        label: "E-Mail",
        width: 220,
        render: (r: AssignmentRow) => r.eigentuemer_email || "-"
      },
      {
        key: "einheit_stimmrecht",
        label: <>Stimmrecht<br />Einheit</>,
        width: 126,
        align: "center" as const,
        render: (r: AssignmentRow) => formatAssignmentNumber(r.einheit_stimmrecht)
      },
      {
        key: "einheit_mea",
        label: <>MEA<br />Einheit</>,
        width: 126,
        align: "center" as const,
        render: (r: AssignmentRow) => formatAssignmentMea(r.einheit_mea)
      },
      {
        key: "status",
        label: "Status",
        width: 112,
        align: "center" as const,
        render: (r: AssignmentRow) => (
          <span
            className={`badge ${r.status} quick-tooltip`}
            data-tooltip={buildAssignmentValidityTooltip(r)}
            title={buildAssignmentValidityTooltip(r)}
            tabIndex={0}
          >
            {r.status}
          </span>
        )
      },
      {
        key: "actions",
        label: "Aktionen",
        render: (r: AssignmentRow) => {
          if (!canUpdate && !canDelete) return <span style={{ color: "var(--text-soft)" }}>-</span>;
          const locked = lockedObjectIds.has(r.objectId);
          return (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {canUpdate ? (
                <button
                  onClick={() =>
                    {
                      setEditForm({
                        id: r.id,
                        objectId: r.objectId,
                        unitId: r.unitId,
                        ownerId: r.ownerId,
                        status: r.status,
                        gueltig_ab: r.gueltig_ab ?? "",
                        gueltig_bis: r.gueltig_bis ?? ""
                      });
                      setOwnerSearchEdit(`${r.eigentuemer_id} ${r.eigentuemername ?? ""}`.trim());
                    }
                  }
                  disabled={locked}
                  title={locked ? "Gesperrt wegen aktivem Umlaufbeschluss" : undefined}
                >
                  Bearbeiten
                </button>
              ) : null}
              {canDelete ? (
                <button
                  onClick={() => {
                    if (locked) {
                      setActionError("Zuordnung ist wegen aktivem Umlaufbeschluss gesperrt.");
                      return;
                    }
                    const activeAssignmentsForUnit = (ownerAssignmentsData?.data ?? []).filter(
                      (a) => a.objectId === r.objectId && a.unitId === r.unitId && a.status === "aktiv" && a.id !== r.id
                    );
                    const removesLastActiveAssignment = r.status === "aktiv" && activeAssignmentsForUnit.length === 0;
                    setDeleteConfirm({
                      id: r.id,
                      label: `${r.objektnummer} / ${r.einheitsnummer} / ${r.eigentuemer_id}`,
                      code: String(Math.floor(1000 + Math.random() * 9000)),
                      input: ["", "", "", ""],
                      error: "",
                      warning: removesLastActiveAssignment
                        ? "Achtung: Nach dem Löschen bleibt auf dieser Einheit keine aktive Eigentümerzuordnung mehr bestehen."
                        : undefined
                    });
                  }}
                  disabled={locked || deleteMutation.isPending}
                  title={locked ? "Löschen bei aktivem Umlaufbeschluss gesperrt" : undefined}
                >
                  Löschen
                </button>
              ) : null}
              {locked && (canUpdate || canDelete) ? <span className="lock-indicator" title="Gesperrt wegen aktivem Umlaufbeschluss">🔒</span> : null}
            </div>
          );
        }
      }
    ],
    [canDelete, canUpdate, deleteMutation.isPending, lockedObjectIds, objectIdByObjektnummer, ownerDisplayById, sortBy, sortDir]
  );

  const canCreateForm = form.objectId && form.unitId && form.ownerId && form.status;
  const canEdit = editForm && editForm.objectId && editForm.unitId && editForm.ownerId && editForm.status;
  const hideAssignmentsList = showCreate || !!editForm;
  const rows = data?.data ?? [];
  const totalRows = data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const isFrameworkV2 =
    uiSettings?.management?.uiFramework === "mockup_v2" ||
    (typeof document !== "undefined" && document.body.classList.contains("framework-v2"));
  const fixedViewportListLayout = isFrameworkV2 && !hideAssignmentsList;
  const pageItems = useMemo(() => {
    if (totalPages <= 1) return [1];
    const start = Math.max(1, page - 1);
    const end = Math.min(totalPages, start + 2);
    const adjustedStart = Math.max(1, end - 2);
    return Array.from({ length: end - adjustedStart + 1 }, (_, i) => adjustedStart + i);
  }, [page, totalPages]);

  const assignmentTitleIcon = (
    <svg viewBox="0 0 24 24" fill="none">
      <circle cx="6.2" cy="7.2" r="2.1" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17.8" cy="16.8" r="2.1" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17.8" cy="7.2" r="2.1" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8.4 7.2h7.2M7.9 8.8l7.4 6.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );

  const icons = {
    search: (
      <svg viewBox="0 0 20 20" fill="none">
        <circle cx="9" cy="9" r="5.8" stroke="currentColor" strokeWidth="1.8" />
        <path d="m13.2 13.2 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
    reset: (
      <svg viewBox="0 0 20 20" fill="none">
        <path d="M5.2 6.3V3.9H2.8M4 4a6.4 6.4 0 1 1-1.4 7.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    object: (
      <svg viewBox="0 0 20 20" fill="none">
        <path d="M6.2 5.2h7.6v9.6H6.2z" stroke="currentColor" strokeWidth="1.7" />
        <path d="M6.2 8.4h7.6M6.2 11.6h7.6M9.4 5.2v9.6M12.6 5.2v9.6" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    ),
    unit: (
      <svg viewBox="0 0 24 24" fill="none">
        <rect x="5.1" y="4.8" width="13.8" height="14.8" rx="2.1" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8.3 9h2.6M13.1 9h2.6M8.3 13.2h2.6M13.1 13.2h2.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M9.2 19.6v-2.9h5.6v2.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    owner: (
      <svg viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="7.4" r="2.5" stroke="currentColor" strokeWidth="1.7" />
        <path d="M5.2 15.9a4.8 4.8 0 0 1 9.6 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    ),
    ownersGroup: (
      <svg viewBox="0 0 24 24" fill="none">
        <circle cx="9.1" cy="9.1" r="2.5" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="15.8" cy="8.4" r="2.1" stroke="currentColor" strokeWidth="1.8" />
        <path d="M4.7 18.6a5 5 0 0 1 8.8 0M12.2 18.6a4.2 4.2 0 0 1 7.1 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
    email: (
      <svg viewBox="0 0 20 20" fill="none">
        <rect x="3.4" y="5.1" width="13.2" height="9.8" rx="1.8" stroke="currentColor" strokeWidth="1.7" />
        <path d="m4.8 6.7 5.2 4.1 5.2-4.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    votes: (
      <svg viewBox="0 0 20 20" fill="none">
        <path d="M4.1 15.9h11.8M5.6 15.9V9.4M10 15.9V6.7M14.4 15.9V4.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    ),
    mea: (
      <svg viewBox="0 0 20 20" fill="none">
        <path d="M10 3.7 15.3 6.8v6.4L10 16.3l-5.3-3.1V6.8L10 3.7Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M10 3.7v12.6M4.7 6.8 10 10l5.3-3.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    status: (
      <svg viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="5.9" stroke="currentColor" strokeWidth="1.7" />
        <path d="m10 7.2 1.8 2.5L10 12.8 8.2 9.7 10 7.2Z" fill="currentColor" />
      </svg>
    ),
    actions: (
      <svg viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="5.3" r="1.4" fill="currentColor" />
        <circle cx="10" cy="10" r="1.4" fill="currentColor" />
        <circle cx="10" cy="14.7" r="1.4" fill="currentColor" />
      </svg>
    )
  };

  const plainHeader = (label: ReactNode, icon: ReactNode) => (
    <span className="resolutions-v2-th-plain">
      <span className="resolutions-v2-th-icon" aria-hidden="true">{icon}</span>
      <span className="resolutions-v2-th-label">{label}</span>
    </span>
  );

  const renderSortIndicator = (field: AssignmentSortField) => (
    <span className="resolutions-v2-sort-indicator" aria-hidden="true">
      {sortBy === field ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  const renderV2SortHeader = (field: AssignmentSortField, label: ReactNode, icon: ReactNode) => (
    <button type="button" className="resolutions-v2-th-sort" onClick={() => toggleSort(field)}>
      <span className="resolutions-v2-th-icon" aria-hidden="true">{icon}</span>
      <span className="resolutions-v2-th-label">{label}</span>
      {renderSortIndicator(field)}
    </button>
  );

  const resetListFilters = () => {
    setSearch("");
    setObjectFilter("");
    setStatus("aktiv");
    setRecipientType("");
    setSortBy("objektnummer");
    setSortDir("asc");
    setPage(1);
  };

  const handleCreate = () => {
    const payload = {
      objectId: form.objectId,
      unitId: form.unitId,
      ownerId: form.ownerId,
      status: form.status as "aktiv" | "inaktiv",
      gueltig_ab: form.gueltig_ab || undefined,
      gueltig_bis: form.gueltig_bis || undefined
    };

    if (payload.status === "aktiv") {
      const activeForUnit = (ownerAssignmentsData?.data ?? []).find(
        (a) => a.objectId === payload.objectId && a.unitId === payload.unitId && a.status === "aktiv" && a.ownerId !== payload.ownerId
      );
      if (activeForUnit) {
        setOwnershipChange({ existing: activeForUnit, vorgaengerGueltigBis: "" });
        setCreateError("");
        return;
      }
    }

    createMutation.mutate(payload);
  };

  const renderOwnerSearchList = (
    rows: OwnerRow[],
    selectedOwnerId: string,
    onSelectOwner: (ownerId: string) => void,
    disabled: boolean
  ) => (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-soft)" }}>Suchergebnisse</div>
      <div style={{ border: "1px solid var(--line)", borderRadius: 6, minHeight: 140, maxHeight: 260, overflowY: "auto", overflowX: "hidden", background: "#fff" }}>
        <table style={{ tableLayout: "fixed", width: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: "11%" }}>Eigentümer-ID</th>
              <th style={{ width: "9%" }}>Anrede</th>
              <th style={{ width: "11%" }}>Vorname</th>
              <th style={{ width: "12%" }}>Name 1</th>
              <th style={{ width: "12%" }}>Name 2</th>
              <th style={{ width: "12%" }}>Name 3</th>
              <th style={{ width: "16%" }}>E-Mail</th>
              <th style={{ width: "17%" }}>Zuordnungen</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((owner) => {
                const selected = selectedOwnerId === owner.id;
                const refs = assignmentRefsByOwner.get(owner.id) ?? [];
              return (
                <tr
                  key={owner.id}
                  onClick={() => !disabled && onSelectOwner(owner.id)}
                  aria-selected={selected}
                  style={{
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.65 : 1,
                    background: selected ? "#e8f1ff" : undefined,
                    boxShadow: selected ? "inset 0 0 0 2px #2f6fed" : undefined,
                    fontWeight: selected ? 600 : 400
                  }}
                >
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
                      {selected ? `Ausgewählt: ${owner.eigentuemer_id}` : owner.eigentuemer_id}
                    </td>
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{owner.anrede || "-"}</td>
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{owner.vorname || "-"}</td>
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{owner.name_1 || "-"}</td>
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{owner.name_2 || "-"}</td>
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{owner.name_3 || "-"}</td>
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{owner.email || "-"}</td>
                    <td style={{ color: "var(--text-soft)", whiteSpace: "normal", overflowWrap: "anywhere" }}>
                      {refs.length ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {refs.slice(0, 3).map((ref) => (
                            <span
                              key={`${owner.id}-${ref}`}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "2px 8px",
                                borderRadius: 999,
                                border: "1px solid #c9d1da",
                                background: "#f2f4f7",
                                color: "#344054",
                                fontSize: 12,
                                lineHeight: 1.2
                              }}
                            >
                              {ref}
                            </span>
                          ))}
                          {refs.length > 3 ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "2px 8px",
                                borderRadius: 999,
                                border: "1px solid #c9d1da",
                                background: "#f2f4f7",
                                color: "#344054",
                                fontSize: 12,
                                lineHeight: 1.2
                              }}
                            >
                              +{refs.length - 3}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} style={{ color: "var(--text-soft)" }}>Keine passenden Eigentümer gefunden.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className={fixedViewportListLayout ? "resolutions-v2-page" : undefined}>
      <section className={`page-header panel ${isFrameworkV2 ? "resolutions-v2-page-header" : ""}`}>
        <h2 className={isFrameworkV2 ? "resolutions-v2-title" : ""} style={{ margin: 0 }}>
          {isFrameworkV2 ? <span className="resolutions-v2-title-icon" aria-hidden="true">{assignmentTitleIcon}</span> : null}
          {isFrameworkV2 ? (
            <span className="resolutions-v2-title-copy">
              <span className="resolutions-v2-title-main">Zuordnungen</span>
              <span className="resolutions-v2-title-sub">Übersicht aller Eigentümer-Zuordnungen</span>
            </span>
          ) : (
            <span>Zuordnungen</span>
          )}
        </h2>
        {canCreate ? (
          <button className={isFrameworkV2 ? "resolutions-v2-create-btn" : ""} onClick={() => setShowCreate((v) => !v)}>
            {isFrameworkV2 && !showCreate ? (
              <span className="resolutions-v2-create-btn-icon" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none">
                  <path d="M10 4.2v11.6M4.2 10h11.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
            ) : null}
            <span>{showCreate ? "Schließen" : "Neue Zuordnung"}</span>
          </button>
        ) : null}
      </section>

      {canCreate && showCreate ? (
        <section className="panel create-form">
          <h3 style={{ marginTop: 0 }}>Zuordnung anlegen</h3>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(260px, 1.6fr) minmax(180px, 1fr) minmax(180px, 1fr) minmax(140px, 0.7fr) minmax(140px, 0.8fr) minmax(140px, 0.8fr)", gap: 10, alignItems: "start" }}>
            <div className="form-field">
              <label>Eigentümer*</label>
              <input
                placeholder="Eigentümer suchen (ID, Name, E-Mail)"
                value={ownerSearchCreate}
                onChange={(e) => setOwnerSearchCreate(e.target.value)}
              />
              <label style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={createOnlyUnassigned}
                  onChange={(e) => setCreateOnlyUnassigned(e.target.checked)}
                />
                Nur ohne Zuordnung
              </label>
            </div>
            <div className="form-field">
              <label>Objekt*</label>
              <select value={form.objectId} onChange={(e) => setForm({ ...form, objectId: e.target.value, unitId: "" })}>
                <option value="">Objekt wählen*</option>
                {(objectsData?.data ?? []).map((o) => (
                  <option key={o.id} value={o.id}>{`${o.objektnummer} - ${o.objektname}${lockedObjectIds.has(o.id) ? " (gesperrt)" : ""}`}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Einheit*</label>
              <select value={form.unitId} onChange={(e) => setForm({ ...form, unitId: e.target.value })} disabled={!form.objectId}>
                <option value="">Einheit wählen*</option>
                {(unitsData?.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>{`${u.einheitsnummer} - ${u.bezeichnung}`}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Status*</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as StatusOption })}>
                <option value="">Status wählen*</option>
                <option value="aktiv">Aktiv</option>
                <option value="inaktiv">Inaktiv</option>
              </select>
            </div>
            <div className="form-field">
              <label>Eigentümer ab</label>
              <input type="date" value={form.gueltig_ab} onChange={(e) => setForm({ ...form, gueltig_ab: e.target.value })} />
            </div>
            <div className="form-field">
              <label>Eigentümer bis</label>
              <input type="date" value={form.gueltig_bis} onChange={(e) => setForm({ ...form, gueltig_bis: e.target.value })} />
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            {renderOwnerSearchList(
              filteredCreateOwners,
              form.ownerId,
              (ownerId) => setForm({ ...form, ownerId }),
              false
            )}
            <small style={{ color: "var(--text-soft)" }}>{filteredCreateOwners.length} Treffer</small>
          </div>
          <div className="form-actions">
            <button disabled={!canCreateForm || createMutation.isPending || lockedObjectIds.has(form.objectId)} title={lockedObjectIds.has(form.objectId) ? "Gesperrt wegen aktivem Umlaufbeschluss" : undefined} onClick={handleCreate}>
              {createMutation.isPending ? "Speichere..." : "Zuordnung speichern"}
            </button>
            {createError ? <span className="error-text">{createError}</span> : null}
          </div>
        </section>
      ) : null}

      {ownershipChange ? (
        <section className="panel create-form">
          <h3 style={{ marginTop: 0 }}>Kollision erkannt: Eigentumswechsel?</h3>
          {(() => {
            const collisionOwner =
              (ownersData?.data ?? []).find((owner) => owner.id === ownershipChange.existing.ownerId) ?? null;
            const collisionOwnerName = collisionOwner
              ? formatOwnerDisplayName(collisionOwner)
              : ownershipChange.existing.eigentuemername || ownershipChange.existing.eigentuemer_id;
            return (
          <p style={{ marginTop: 0 }}>
                Für diese Einheit existiert bereits eine aktive Zuordnung ({ownershipChange.existing.objektnummer}/{ownershipChange.existing.einheitsnummer} an {collisionOwnerName}).
            Wenn es ein Eigentumswechsel ist, wird der bisherige Eigentümer auf inaktiv gesetzt. Optional können Sie ein Bis-Datum angeben; dann startet die neue Zuordnung automatisch am Folgetag.
          </p>
            );
          })()}
          <div className="form-field" style={{ maxWidth: 260 }}>
            <label>Bisheriger Eigentümer bis (optional)</label>
            <input
              type="date"
              value={ownershipChange.vorgaengerGueltigBis}
              onChange={(e) => setOwnershipChange({ ...ownershipChange, vorgaengerGueltigBis: e.target.value })}
            />
          </div>
          <div className="form-actions">
            <button
              onClick={() => {
                createMutation.mutate({
                  objectId: form.objectId,
                  unitId: form.unitId,
                  ownerId: form.ownerId,
                  status: form.status as "aktiv" | "inaktiv",
                  gueltig_ab: form.gueltig_ab || undefined,
                  gueltig_bis: form.gueltig_bis || undefined,
                  uebernahme_von_assignment_id: ownershipChange.existing.id,
                  vorgaenger_gueltig_bis: ownershipChange.vorgaengerGueltigBis || undefined
                });
              }}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? "Übernehme..." : "Ja, Eigentumswechsel durchführen"}
            </button>
            <button
              onClick={() => {
                setOwnershipChange(null);
              }}
              disabled={createMutation.isPending}
            >
              Abbrechen
            </button>
          </div>
        </section>
      ) : null}

      {canUpdate && editForm ? (
        <section className="panel create-form">
          <h3 style={{ marginTop: 0 }}>Zuordnung bearbeiten</h3>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(260px, 1.6fr) minmax(180px, 1fr) minmax(180px, 1fr) minmax(140px, 0.7fr) minmax(140px, 0.8fr) minmax(140px, 0.8fr)", gap: 10, alignItems: "start" }}>
            <div className="form-field">
              <label>Eigentümer*</label>
              <input
                placeholder="Eigentümer suchen (ID, Name, E-Mail)"
                value={ownerSearchEdit}
                onChange={(e) => setOwnerSearchEdit(e.target.value)}
              />
              <label style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={editOnlyUnassigned}
                  onChange={(e) => setEditOnlyUnassigned(e.target.checked)}
                />
                Nur ohne Zuordnung
              </label>
            </div>
            <div className="form-field">
              <label>Objekt*</label>
              <select
                value={editForm.objectId}
                onChange={(e) => setEditForm({ ...editForm, objectId: e.target.value, unitId: "" })}
              >
                <option value="">Objekt wählen*</option>
                {(objectsData?.data ?? []).map((o) => (
                  <option key={o.id} value={o.id}>{`${o.objektnummer} - ${o.objektname}${lockedObjectIds.has(o.id) ? " (gesperrt)" : ""}`}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Einheit*</label>
              <select
                value={editForm.unitId}
                onChange={(e) => setEditForm({ ...editForm, unitId: e.target.value })}
                disabled={!editForm.objectId}
              >
                <option value="">Einheit wählen*</option>
                {(editUnitsData?.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>{`${u.einheitsnummer} - ${u.bezeichnung}`}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Status*</label>
              <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value as StatusOption })}>
                <option value="">Status wählen*</option>
                <option value="aktiv">Aktiv</option>
                <option value="inaktiv">Inaktiv</option>
              </select>
            </div>
            <div className="form-field">
              <label>Eigentümer ab</label>
              <input type="date" value={editForm.gueltig_ab} onChange={(e) => setEditForm({ ...editForm, gueltig_ab: e.target.value })} />
            </div>
            <div className="form-field">
              <label>Eigentümer bis</label>
              <input type="date" value={editForm.gueltig_bis} onChange={(e) => setEditForm({ ...editForm, gueltig_bis: e.target.value })} />
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            {renderOwnerSearchList(
              filteredEditOwners,
              editForm.ownerId,
              (ownerId) => {
                setEditForm({ ...editForm, ownerId });
                const selectedOwner = (editOwnersData?.data ?? []).find((owner) => owner.id === ownerId);
                if (selectedOwner) {
                  setOwnerSearchEdit(`${selectedOwner.eigentuemer_id} ${selectedOwner.vollname ?? ""}`.trim());
                }
              },
              false
            )}
            <small style={{ color: "var(--text-soft)" }}>{filteredEditOwners.length} Treffer</small>
          </div>
          <div className="form-actions">
            <button disabled={!canEdit || editMutation.isPending || (editForm ? lockedObjectIds.has(editForm.objectId) : false)} title={editForm && lockedObjectIds.has(editForm.objectId) ? "Gesperrt wegen aktivem Umlaufbeschluss" : undefined} onClick={() => editMutation.mutate()}>
              {editMutation.isPending ? "Speichere..." : "Änderungen speichern"}
            </button>
            <button onClick={() => setEditForm(null)}>Abbrechen</button>
            {editError ? <span className="error-text">{editError}</span> : null}
          </div>
        </section>
      ) : null}

      {!hideAssignmentsList ? (
        <>
          {isError ? <section className="panel">Fehler beim Laden der Zuordnungen.</section> : null}
          {actionError ? <section className="panel error-text">{actionError}</section> : null}
          {isFrameworkV2 ? (
            <section className="panel resolutions-v2-list-panel">
              <div className="resolutions-v2-filterbar">
                <div className="resolutions-v2-filter-fields">
                  <label className="resolutions-v2-filter-input">
                    <span className="resolutions-v2-filter-icon" aria-hidden="true">{icons.search}</span>
                    <input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} />
                  </label>
                  <select value={objectFilter} onChange={(e) => setObjectFilter(e.target.value)}>
                    <option value="">Alle Objekt-Nr</option>
                    {(objectsData?.data ?? []).map((o) => (
                      <option key={o.id} value={o.id}>{o.objektnummer}</option>
                    ))}
                  </select>
                  <select value={status} onChange={(e) => setStatus(e.target.value as StatusOption)}>
                    <option value="">Alle Status</option>
                    <option value="aktiv">Aktiv</option>
                    <option value="inaktiv">Inaktiv</option>
                  </select>
                  <select value={recipientType} onChange={(e) => setRecipientType(e.target.value as RecipientTypeOption)}>
                    <option value="">Alle</option>
                    <option value="owner">Eigentümer</option>
                    <option value="authorized">Zustellungsbevollmächtigt</option>
                  </select>
                </div>
                <button type="button" className="resolutions-v2-reset-btn" onClick={resetListFilters}>
                  <span className="resolutions-v2-reset-btn-icon" aria-hidden="true">{icons.reset}</span>
                  <span>Filter zurücksetzen</span>
                </button>
              </div>

              <div className="resolutions-v2-row-count">Zeilen: {rows.length} / Gesamt: {totalRows}</div>

              <div className="resolutions-v2-table-wrap">
                <table className="resolutions-v2-table assignments-v2-table">
                  <colgroup>
                    <col style={{ width: "126px" }} />
                    <col style={{ width: "152px" }} />
                    <col style={{ width: "184px" }} />
                    <col />
                    <col style={{ width: "220px" }} />
                    <col style={{ width: "126px" }} />
                    <col style={{ width: "126px" }} />
                    <col style={{ width: "112px" }} />
                    <col style={{ width: "92px" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{renderV2SortHeader("objektnummer", "Objekt-Nr", icons.object)}</th>
                      <th>{renderV2SortHeader("einheitsnummer", "Einheit-Nr", icons.unit)}</th>
                      <th>{renderV2SortHeader("eigentuemer_id", "Eigentümer-ID", icons.owner)}</th>
                      <th>{plainHeader("Eigentümername", icons.ownersGroup)}</th>
                      <th>{plainHeader("E-Mail", icons.email)}</th>
                      <th>{plainHeader("Stimmrecht", icons.votes)}</th>
                      <th>{plainHeader("MEA", icons.mea)}</th>
                      <th>{plainHeader("Status", icons.status)}</th>
                      <th className="resolutions-v2-actions-head" aria-label="Aktionen" title="Aktionen">{plainHeader("", icons.actions)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const objectId = objectIdByObjektnummer.get(r.objektnummer);
                      const locked = lockedObjectIds.has(r.objectId);
                      const menuId = r.id;
                      return (
                        <tr key={r.id}>
                          <td>{objectId ? <Link to={`/objects/${objectId}`} className="badge object-number" style={{ textDecoration: "none" }}>{r.objektnummer}</Link> : <span className="badge object-number">{r.objektnummer}</span>}</td>
                          <td>{formatEinheitsnummer(r.einheitsnummer)}</td>
                          <td>{r.eigentuemer_id}</td>
                          <td className="resolutions-v2-object-cell">
                            {ownerDisplayById.get(r.ownerId) ?? r.eigentuemername ?? "-"}
                            {r.isAuthorizedOwnerRecipient ? <span className="badge" style={{ marginLeft: 8 }}>Zustellungsbevollmächtigt</span> : null}
                          </td>
                          <td>{r.eigentuemer_email || "-"}</td>
                          <td>{formatAssignmentNumber(r.einheit_stimmrecht)}</td>
                          <td>{formatAssignmentMea(r.einheit_mea)}</td>
                          <td>
                            <span className={`badge ${r.status} quick-tooltip`} data-tooltip={buildAssignmentValidityTooltip(r)} title={buildAssignmentValidityTooltip(r)} tabIndex={0}>
                              {r.status}
                            </span>
                          </td>
                          <td>
                            {!canUpdate && !canDelete ? (
                              <span style={{ color: "var(--text-soft)" }}>-</span>
                            ) : (
                              <div className="resolutions-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="resolutions-v2-action-trigger"
                                  aria-label={`Aktionen für Zuordnung ${r.einheitsnummer}`}
                                  aria-expanded={openActionMenuId === menuId}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (openActionMenuId === menuId) {
                                      setOpenActionMenuId(null);
                                      return;
                                    }
                                    openActionMenuAtTrigger(menuId, e.currentTarget, canUpdate && canDelete ? 96 : 52);
                                  }}
                                >
                                  ⋮
                                </button>
                                {openActionMenuId === menuId && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                                  <div
                                    className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed"
                                    style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onMouseEnter={() => setIsActionMenuHovered(true)}
                                    onMouseLeave={() => setIsActionMenuHovered(false)}
                                  >
                                    {canUpdate ? (
                                      <button
                                        type="button"
                                        disabled={locked}
                                        onClick={() => {
                                          setEditForm({
                                            id: r.id,
                                            objectId: r.objectId,
                                            unitId: r.unitId,
                                            ownerId: r.ownerId,
                                            status: r.status,
                                            gueltig_ab: r.gueltig_ab ?? "",
                                            gueltig_bis: r.gueltig_bis ?? ""
                                          });
                                          setOwnerSearchEdit(`${r.eigentuemer_id} ${r.eigentuemername ?? ""}`.trim());
                                          setOpenActionMenuId(null);
                                        }}
                                      >
                                        Bearbeiten
                                      </button>
                                    ) : null}
                                    {canDelete ? (
                                      <button
                                        type="button"
                                        disabled={locked || deleteMutation.isPending}
                                        onClick={() => {
                                          if (locked) {
                                            setActionError("Zuordnung ist wegen aktivem Umlaufbeschluss gesperrt.");
                                            setOpenActionMenuId(null);
                                            return;
                                          }
                                          const activeAssignmentsForUnit = (ownerAssignmentsData?.data ?? []).filter(
                                            (a) => a.objectId === r.objectId && a.unitId === r.unitId && a.status === "aktiv" && a.id !== r.id
                                          );
                                          const removesLastActiveAssignment = r.status === "aktiv" && activeAssignmentsForUnit.length === 0;
                                          setDeleteConfirm({
                                            id: r.id,
                                            label: `${r.objektnummer} / ${r.einheitsnummer} / ${r.eigentuemer_id}`,
                                            code: String(Math.floor(1000 + Math.random() * 9000)),
                                            input: ["", "", "", ""],
                                            error: "",
                                            warning: removesLastActiveAssignment
                                              ? "Achtung: Nach dem Löschen bleibt auf dieser Einheit keine aktive Eigentümerzuordnung mehr bestehen."
                                              : undefined
                                          });
                                          setOpenActionMenuId(null);
                                        }}
                                      >
                                        Löschen
                                      </button>
                                    ) : null}
                                  </div>,
                                  document.body
                                ) : null}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="resolutions-v2-pagination">
                <div className="resolutions-v2-page-size">
                  <select value={String(pageSize)} onChange={(e) => setPageSize(Number(e.target.value) || 10)}>
                    <option value="10">10 pro Seite</option>
                    <option value="20">20 pro Seite</option>
                    <option value="50">50 pro Seite</option>
                  </select>
                </div>
                <div className="resolutions-v2-page-center">
                  <button type="button" className="resolutions-v2-page-arrow" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={page <= 1}>‹</button>
                  {pageItems.map((pageItem) => (
                    <button key={pageItem} type="button" className={`resolutions-v2-page-chip ${pageItem === page ? "active" : ""}`} onClick={() => setPage(pageItem)}>{pageItem}</button>
                  ))}
                  <button type="button" className="resolutions-v2-page-arrow" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={page >= totalPages}>›</button>
                  <span className="resolutions-v2-page-info">Seite {page} von {totalPages}</span>
                </div>
                <div className="resolutions-v2-page-next-wrap">
                  <button type="button" className="resolutions-v2-next-btn" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={page >= totalPages}>
                    <span>Weiter</span>
                    <span aria-hidden="true">›</span>
                  </button>
                </div>
              </div>
            </section>
          ) : (
            <ServerDataTable
              columns={columns}
              rows={rows}
              total={totalRows}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              bodyMaxHeight={560}
              topContent={
              <section className="filterbar" style={{ margin: 0 }}>
                <input placeholder="Suche" value={search} onChange={(e) => setSearch(e.target.value)} />
                <select value={objectFilter} onChange={(e) => setObjectFilter(e.target.value)}>
                  <option value="">Alle Objekt-Nr</option>
                  {(objectsData?.data ?? []).map((o) => (
                    <option key={o.id} value={o.id}>{o.objektnummer}</option>
                  ))}
                </select>
                <select value={status} onChange={(e) => setStatus(e.target.value as StatusOption)}>
                  <option value="">Alle Status</option>
                  <option value="aktiv">Aktiv</option>
                  <option value="inaktiv">Inaktiv</option>
                </select>
                <select value={recipientType} onChange={(e) => setRecipientType(e.target.value as RecipientTypeOption)}>
                  <option value="">Alle</option>
                  <option value="owner">Eigentümer</option>
                  <option value="authorized">Zustellungsbevollmächtigt</option>
                </select>
                </section>
              }
            />
          )}
        </>
      ) : null}

      <DeleteCodeConfirmModal
        open={!!deleteConfirm}
        message={
          deleteConfirm
            ? `${deleteConfirm.warning ? `${deleteConfirm.warning} ` : ""}Zuordnung ${deleteConfirm.label} soll gelöscht werden.`
            : ""
        }
        code={deleteConfirm?.code ?? ""}
        input={deleteConfirm?.input ?? ["", "", "", ""]}
        error={deleteConfirm?.error}
        isPending={deleteMutation.isPending}
        onClose={() => setDeleteConfirm(null)}
        onInputChange={(next) => {
          if (!deleteConfirm) return;
          setDeleteConfirm({ ...deleteConfirm, input: next });
        }}
        onErrorReset={() => {
          if (!deleteConfirm || !deleteConfirm.error) return;
          setDeleteConfirm({ ...deleteConfirm, error: "" });
        }}
        onConfirm={() => {
          if (!deleteConfirm) return;
          const enteredCode = deleteConfirm.input.join("");
          if (enteredCode !== deleteConfirm.code) {
            setDeleteConfirm({ ...deleteConfirm, error: "Sicherheitscode falsch eingegeben." });
            return;
          }
          deleteMutation.mutate(deleteConfirm.id);
        }}
      />
    </div>
  );
};
